import {
  type DatabaseEntry,
  type DatabaseField,
  type DatabaseValue
} from '@databases/types/database';
import {
  type DatabaseViewSettings,
  shouldShowField
} from '@databases/types/database/view';

/**
 * Consumer-owned reads for the initial table. Adapters translate a concrete source;
 * they do not select a database, own view state, or know about selections.
 * Field defaults and stored values use addressed reads, so mounted cells do not
 * materialize whole entries. While active, these synchronous readers operate on
 * loaded data: `undefined` means absence, never loading or failure. Partial-data
 * adapters need an explicit availability contract before using this boundary.
 * Live acquisition and coherent snapshot publication remain deferred.
 */
export interface TableV2DataSource {
  listEntryIds(): readonly string[];
  listFields(): readonly DatabaseField[];
  getEntry(entryId: string): DatabaseEntry | undefined;
  getDefaultValue(fieldId: string): DatabaseValue | undefined;
  getValue(address: { entryId: string; fieldId: string }): DatabaseValue | undefined;
}

export interface TableV2Projection {
  readonly entryIds: readonly string[];
  readonly fields: readonly DatabaseField[];
  readonly visibleFieldIds: readonly string[];
}

export type TableV2Reads = Pick<TableV2DataSource, 'getDefaultValue' | 'getValue'>;

/**
 * One view generation. Its projection fixes initial coordinate ordering, not
 * canonical values: addressed entry reads remain lazy against the bound source.
 * Composition disposes the instance before replacing its source or selected view.
 * Retained callbacks then return undefined without reading any source; a disposed
 * instance must not be consumed as an available source of defaulted cell content.
 */
export interface TableV2Instance {
  readonly projection: TableV2Projection;
  getEntry(entryId: string): DatabaseEntry | undefined;
  getDefaultValue(fieldId: string): DatabaseValue | undefined;
  getValue(address: { entryId: string; fieldId: string }): DatabaseValue | undefined;
  dispose(): void;
}

/**
 * Captures one initial ordering and lends revocable readers to its consumers.
 * Application composition calls dispose before replacing the source/view; this
 * factory neither selects a source nor owns its SDK or selection lifetime.
 */
export function createTableV2Instance(
  source: TableV2DataSource,
  visibility: DatabaseViewSettings['fields']
): TableV2Instance {
  let activeSource: TableV2DataSource | undefined = source;
  const fields = Object.freeze([...source.listFields()]);
  const projection = Object.freeze({
    fields,
    entryIds: Object.freeze([...source.listEntryIds()]),
    visibleFieldIds: Object.freeze(
      fields.filter(({ id }) => shouldShowField(visibility, id)).map(({ id }) => id)
    )
  });

  return {
    projection,
    getEntry: (entryId) => activeSource?.getEntry(entryId),
    getDefaultValue: (fieldId) => activeSource?.getDefaultValue(fieldId),
    getValue: (address) => activeSource?.getValue(address),
    dispose: () => {
      activeSource = undefined;
    }
  };
}
